module Widgets.Signature.Types where

import Common

import GraphQL.Shape (shapeOf, shapeToStringInner)
import GraphQL.Type (NamedType, RenamedType(..), TypedID, TypedString)
import Type.Prelude (Proxy(..))
import Widgets.Signature.Pad (Signature)

type SignID = TypedID "Signature"
type SignKey = TypedString "SignatureKey"


type SignatureFieldPolicy =
    { create :: Boolean
    , update :: Boolean
    , request :: Boolean
    , saveDefault :: Boolean
    , useDefault :: Boolean
    }

type SignatureFieldBase r =
    { key :: SignKey
    , label :: String
    , emailLabel :: String
    , relationship :: SigRelation
    , can :: SignatureFieldPolicy
    | r
    }

-- | Defines a signature field to be shown on a form.
type SignatureField = SignatureFieldBase
    ( relationship_label :: String )

-- | Signature field in a form type.
type FormSigField = SignatureFieldBase
    ( relationship_label :: String )

-- | Signature field in a document/form.
type DocSigField = SignatureFieldBase
    ( default_email :: Maybe String
    , default_first_name :: Maybe String
    , default_last_name :: Maybe String
    )

formSigFieldFields :: String
formSigFieldFields = shapeToStringInner $ shapeOf (Proxy :: Proxy FormSigField)

normalizeFormSigField :: FormSigField -> SignatureField
normalizeFormSigField { key, label, emailLabel, relationship, relationship_label, can } =
    { key, label, emailLabel, relationship, relationship_label, can }

-- | Document signature fields carry defaults but not `relationship_label`, so
-- | widget rendering fills that in here.
normalizeDocSigField :: DocSigField -> SignatureField
normalizeDocSigField { key, label, emailLabel, relationship, can } =
    { key
    , label
    , emailLabel
    , relationship
    , relationship_label: sigRelName relationship
    , can
    }

-- | Base record for signatures.
type SignatureBase r =
    { first_name :: String
    , last_name :: String
    , email :: String
    | r
    }

type SigRelation = NamedType "SignatureRelationship" String

sigRelName :: SigRelation -> String
sigRelName (RenamedType "STUDENT_SIGNATURE") = lookupTranslation "rel-student"
sigRelName (RenamedType "TEACHER_SIGNATURE") = lookupTranslation "rel-teacher"
sigRelName (RenamedType "EMPLOYER_SIGNATURE") = lookupTranslation "rel-employer"
sigRelName (RenamedType "PARENT_SIGNATURE") = lookupTranslation "rel-parent"
sigRelName (RenamedType "PRINCIPAL_SIGNATURE") = lookupTranslation "rel-principal"
sigRelName (RenamedType "COORDINATOR_SIGNATURE") = lookupTranslation "rel-coordinator"
sigRelName _ = ""

employerRel :: SigRelation
employerRel = RenamedType "EMPLOYER_SIGNATURE"

-- | A complete form signature with attached PNG binary blob.
type FormSignature = SignatureBase ( signature :: Signature )

-- | Contains just the information about a signature such as name and email,
-- | without the actual signature.
type SignatureInfo = SignatureBase ()

-- | A signature which may or may not have a signature image attached.
type PartialSignature = SignatureBase ( signature :: Maybe Signature )

emptySignature :: PartialSignature
emptySignature =
    { first_name: ""
    , last_name: ""
    , email: ""
    , signature: Nothing
    }

emptySigInfo :: SignatureInfo
emptySigInfo =
    { first_name: ""
    , last_name: ""
    , email: ""
    }

isSigned :: PartialSignature -> Boolean
isSigned { signature: Nothing } = false
isSigned _ = true

-- | Takes a signature record and adds or replaces the signature field with the
-- | given signature image.
-- | 
-- | This can construct either a full `FormSignature` or a `PartialSignature`,
-- | depending on whether the `sig` type variable is `Signature` or `Maybe
-- | Signature`.
addSignature :: forall sig r. SignatureBase r -> sig -> SignatureBase ( signature :: sig )
addSignature { first_name, last_name, email } signature =
    { first_name, last_name, email, signature }

-- | Splits a combined signature into its info and signature parts.
splitSignature :: forall sig. SignatureBase (signature :: sig) -> Tuple sig SignatureInfo
splitSignature { first_name, last_name, email, signature } =
    Tuple signature { first_name, last_name, email }

-- | Converts a partial signature into a full form signature if it is signed.
-- | Otherwise, returns null.
fromPartSig :: PartialSignature -> Maybe FormSignature
fromPartSig { signature: Nothing } = Nothing
fromPartSig part@{ signature: Just sig } = Just $ addSignature part sig

-- | Converts a form signature into a partial signature.
toPartSig :: FormSignature -> PartialSignature
toPartSig full@{ signature } = addSignature full $ Just signature
